import { useState, useCallback, useRef, useEffect } from 'react';
import { LeadFormData, WorkflowState, LeadData, WorkflowStep } from '../types';
import apiService from '../services/apiService';
import axios from 'axios';

const initialWorkflowState: WorkflowState = {
  steps: [
    { id: 'finding', name: 'Finding Businesses', status: 'pending' },
    { id: 'analyzing', name: 'Finding Contacts', status: 'pending' },
    { id: 'displaying', name: 'Export Results', status: 'pending' }
  ],
  currentStepId: null,
  results: [],
  isComplete: false,
  formData: null
};

export const useWorkflow = () => {
  const [state, setState] = useState<WorkflowState>(initialWorkflowState);
  const [formData, setFormData] = useState<LeadFormData | null>(null);
  const cancelRef = useRef(false);
  const [isAutoLoadingMore, setIsAutoLoadingMore] = useState<boolean>(false);
  const [noMoreResults, setNoMoreResults] = useState<boolean>(false);
  const loadMoreResultsRef = useRef<((isAutoLoading?: boolean) => Promise<LeadData[] | undefined>) | null>(null);

  const updateStep = useCallback((stepId: string, status: WorkflowStep['status'], message?: string) => {
    setState(prevState => ({
      ...prevState,
      steps: prevState.steps.map(step => 
        step.id === stepId ? { ...step, status, message } : step
      )
    }));
  }, []);

  const setCurrentStep = useCallback((stepId: string) => {
    setState(prevState => ({
      ...prevState,
      currentStepId: stepId
    }));
  }, []);

  const resetWorkflow = useCallback(() => {
    setState(initialWorkflowState);
    setFormData(null);
    setNoMoreResults(false);
  }, []);

  const startWorkflow = useCallback(async (data: LeadFormData) => {
    setFormData(data);
    setState({
      ...initialWorkflowState,
      currentStepId: 'finding',
      formData: data
    });
    
    // Reset cancellation flag
    cancelRef.current = false;
    
    // Function to check if workflow has been cancelled
    const checkCancellation = () => {
      if (cancelRef.current) {
        throw new Error('Workflow cancelled');
      }
    };

    try {
      updateStep('finding', 'in-progress');
      setCurrentStep('finding');
      
      const businesses = await apiService.findBusinessesInArea(data, undefined, apiService.createCancelToken());
      
      // Check if cancelled after finding businesses
      checkCancellation();
      
      updateStep('finding', 'completed', `Found ${businesses.length} businesses total`);

      // Check if we have a message and suggestions from the API
      let message = '';
      let suggestions: string[] = [];
      const id = businesses.id;
      
      if ('message' in businesses && typeof businesses.message === 'string') {
        message = businesses.message;
        
        if ('suggestions' in businesses && Array.isArray(businesses.suggestions)) {
          suggestions = businesses.suggestions;
        }
        
        // Update state with message and suggestions
        setState(prevState => ({
          ...prevState,
          message,
          suggestions,
          id
        }));
        
        // If there are no businesses, we're done
        if (businesses.length === 0) {
          setState(prevState => ({
            ...prevState,
            isComplete: true,
            currentStepId: null
          }));
          return;
        }
      }

      const initialResults: LeadData[] = businesses.map(business => ({
        ...business,
        teamMembers: [],
        businessSocialLinks: business.socialLinks || [],
        isLoadingContacts: false
      }));

      // Update state with results immediately after finding businesses
      setState(prevState => ({
        ...prevState,
        results: initialResults,
        // Set isComplete to true so results are displayed, but keep workflow running
        isComplete: true,
        id
      }));

      // Check if we need to auto-load more results to reach the target
      if (data.maxResults && initialResults.length < data.maxResults) {
        console.log(`Initial results (${initialResults.length}) less than target (${data.maxResults}), starting auto-load`);
        // Wait a moment before starting auto-loading
        setTimeout(async () => {
          try {
            if (loadMoreResultsRef.current) {
              await loadMoreResultsRef.current();
            }
          } catch (error) {
            console.error('Error auto-loading more results after initial search:', error);
          }
        }, 1000);
      }

      // Check if cancelled before moving to analyzing step
      checkCancellation();
      
      // Move to analyzing step
      updateStep('analyzing', 'in-progress');
      setCurrentStep('analyzing');
      
      // Find contacts for all businesses with websites
      const businessesWithWebsites = initialResults.filter(business => business.website && business.website !== '#');
      
      if (businessesWithWebsites.length > 0) {
        try {
          // Update step to show we're starting
          updateStep('analyzing', 'in-progress', `Finding contacts for ${businessesWithWebsites.length} businesses...`);
          
          // Set isLoadingContacts to true for all businesses with websites before starting the contact fetching process
          setState(prevState => ({
            ...prevState,
            results: prevState.results.map((item) => {
              const businessIndex = businessesWithWebsites.findIndex(b => b.name === item.name && b.address === item.address);
              return businessIndex >= 0 
                ? { ...item, isLoadingContacts: true } 
                : item;
            })
          }));
          
          let completedCount = 0;
          
          // Process businesses in parallel
          const contactPromises = businessesWithWebsites.map(async (business) => {
            try {
              // Create instructions from form data
              const instructions = state.formData ? 
                `Find contacts for ${business.name} according to these criteria: ${state.formData.instructions || 'No specific criteria'}` : 
                undefined;
              
              const newContacts = await apiService.findContacts(
                business.website!, 
                apiService.createCancelToken(),
                instructions,
                business.socialLinks || []
              );
              
              // Increment completed count and update step message
              completedCount++;
              updateStep('analyzing', 'in-progress', `Found contacts for ${completedCount}/${businessesWithWebsites.length} businesses`);
              
              // Update this business's contacts immediately as they're found
              setState(prevState => ({
                ...prevState,
                results: prevState.results.map(item => {
                  // Find the correct business by matching name and address
                  const isMatchingBusiness = item.name === business.name && item.address === business.address;
                  return isMatchingBusiness
                    ? { 
                        ...item, 
                        teamMembers: newContacts,
                        isLoadingContacts: false,
                        contactsMessage: newContacts.length === 0 ? 'No contacts found' : undefined,
                        // Store the description from find-contacts-crawl in aboutUs instead of overwriting description
                        aboutUs: newContacts.description || item.aboutUs,
                        email: newContacts.email || (item as { email?: string }).email,
                        businessSocialLinks: [
                          ...(item.businessSocialLinks || []),
                          ...(newContacts.socialLinks || []).filter(
                            link => !(item.businessSocialLinks || []).includes(link)
                          )
                        ],
                        // Add relevancy score if available
                        ...(newContacts.relevancyScore !== undefined && { 
                          matchesInstructions: newContacts.relevancyScore > 50,
                          relevancyScore: newContacts.relevancyScore
                        })
                      } 
                    : item;
                })
              }));
              
              // Find the correct index in the results array for the return value
              const resultIndex = state.results.findIndex(item => 
                item.name === business.name && item.address === business.address
              );
              
              return { index: resultIndex !== -1 ? resultIndex : businessesWithWebsites.indexOf(business), contacts: newContacts, error: null };
            } catch (error) {
              console.error(`Error finding contacts for ${business.name}:`, error);
              
              // Still increment completed count even on error
              completedCount++;
              updateStep('analyzing', 'in-progress', `Found contacts for ${completedCount}/${businessesWithWebsites.length} businesses`);
              
              // Update this business with the error immediately
              setState(prevState => ({
                ...prevState,
                results: prevState.results.map(item => {
                  // Find the correct business by matching name and address
                  const isMatchingBusiness = item.name === business.name && item.address === business.address;
                  return isMatchingBusiness
                    ? { 
                        ...item, 
                        isLoadingContacts: false,
                        contactsError: error instanceof Error ? error.message : 'An error occurred finding contacts'
                      } 
                    : item;
                })
              }));
              
              return { 
                index: state.results.findIndex(item => item.name === business.name && item.address === business.address), 
                contacts: [], 
                error: error instanceof Error ? error.message : 'An error occurred finding contacts' 
              };
            }
          });
          
          // Wait for all contact finding operations to complete
          await Promise.all(contactPromises);
          
          // Calculate total stats after all contacts have been found
          // Count businesses with contacts in the entire result set
          const businessesWithContacts = state.results.filter(business => 
            business.teamMembers && business.teamMembers.length > 0
          ).length;
          
          // Count businesses with websites in the entire result set
          const totalBusinessesWithWebsites = businessesWithWebsites.length;
          
          updateStep('analyzing', 'completed', `Found contacts for ${businessesWithContacts}/${totalBusinessesWithWebsites} businesses`);
        } catch (error) {
          console.error('Error finding contacts:', error);
          updateStep('analyzing', 'error', 'Error finding contacts');
        }
      } else {
        updateStep('analyzing', 'completed', 'No websites available to find contacts');
      }
      
      // Check if cancelled before moving to displaying step
      checkCancellation();
      
      // Move to displaying step
      updateStep('displaying', 'in-progress');
      setCurrentStep('displaying');
      
      // Mark the displaying step as completed
      updateStep('displaying', 'completed', 'Results ready for export');
      
      setState(prevState => ({
        ...prevState,
        currentStepId: null
      }));

      // We've already started auto-loading earlier, no need to do it again here
    } catch (error) {
      console.error('Error in workflow:', error);
      
      // Only update state if not cancelled
      if (error instanceof Error && error.message === 'Workflow cancelled') {
        // Workflow was cancelled, state is already updated by cancelWorkflow
        return;
      }
      
      // Don't show error message for cancelled requests
      if (axios.isCancel(error)) {
        console.log('Request cancelled:', error.message);
        return;
      }
      
      setState(prevState => ({
        ...prevState,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
        isComplete: true,
        currentStepId: null
      }));
    }
  }, [updateStep, setCurrentStep]);

  // Function to find contacts for a specific business
  const findBusinessContacts = useCallback(async (businessIndex: number) => {
    try {
      // Get the business from the state
      const business = state.results[businessIndex];
      
      if (!business) {
        console.error('Cannot find contacts: Business not found');
        return;
      }
      
      if (!business.website || business.website === '#') {
        console.error('Cannot find contacts: Business has no website');
        
        // Update the business to show the error
        setState(prevState => ({
          ...prevState,
          results: prevState.results.map((item, index) => 
            index === businessIndex 
              ? { 
                  ...item, 
                  contactsError: 'No website available to find contacts'
                } 
              : item
          )
        }));
        
        return;
      }
      
      // Update the workflow step if we're in the analyzing step
      if (state.currentStepId === 'analyzing') {
        updateStep('analyzing', 'in-progress', `Finding contacts for ${business.name}...`);
      }
      
      // Update the business to show it's loading contacts
      setState(prevState => ({
        ...prevState,
        results: prevState.results.map((item, index) => 
          index === businessIndex 
            ? { ...item, isLoadingContacts: true, contactsError: undefined, contactsMessage: undefined } 
            : item
        )
      }));
      
      // Create instructions from form data
      const instructions = state.formData ? 
        `Find contacts for ${business.name} according to these criteria: ${state.formData.instructions || 'No specific criteria'}` : 
        undefined;
      
      // Find contacts for the business
      const newContacts = await apiService.findContacts(
        business.website, 
        apiService.createCancelToken(),
        instructions,
        business.businessSocialLinks || []
      );
      
      // Get existing team members
      const existingTeamMembers = business.teamMembers || [];
      
      // Merge existing and new contacts
      const mergedContacts = [...existingTeamMembers];
      
      // Process each new contact
      newContacts.forEach(newContact => {
        // Try to find an existing contact with the same name
        const existingContactIndex = mergedContacts.findIndex(
          contact => contact.name.toLowerCase() === newContact.name.toLowerCase()
        );
        
        if (existingContactIndex >= 0) {
          // Merge with existing contact - keep existing data but add any new data
          const existingContact = mergedContacts[existingContactIndex];
          
          // Create merged contact by combining both, with new data taking precedence only if existing data is empty
          mergedContacts[existingContactIndex] = {
            ...existingContact,
            role: existingContact.role || newContact.role,
            email: existingContact.email || newContact.email,
            phone: existingContact.phone || newContact.phone,
            bio: existingContact.bio || newContact.bio,
            // Merge social links without duplicates
            socialLinks: [
              ...(existingContact.socialLinks || []),
              ...(newContact.socialLinks || []).filter(
                link => !(existingContact.socialLinks || []).includes(link)
              )
            ]
          };
        } else {
          // Add new contact
          mergedContacts.push(newContact);
        }
      });
      
      // Update the business with the merged contacts and new business information
      setState(prevState => ({
        ...prevState,
        results: prevState.results.map((item, index) => 
          index === businessIndex 
            ? { 
                ...item, 
                teamMembers: mergedContacts,
                isLoadingContacts: false,
                contactsMessage: mergedContacts.length === 0 ? 'No contacts found' : undefined,
                // Store the description from find-contacts-crawl in aboutUs instead of overwriting description
                aboutUs: newContacts.description || item.aboutUs,
                email: newContacts.email || (item as { email?: string }).email,
                businessSocialLinks: [
                  ...(item.businessSocialLinks || []),
                  ...(newContacts.socialLinks || []).filter(
                    link => !(item.businessSocialLinks || []).includes(link)
                  )
                ],
                // Add relevancy score if available
                ...(newContacts.relevancyScore !== undefined && { 
                  matchesInstructions: newContacts.relevancyScore > 50,
                  relevancyScore: newContacts.relevancyScore
                })
              } 
            : item
        )
      }));
      
      // Update the workflow step if we're in the analyzing step
      if (state.currentStepId === 'analyzing') {
        const contactsFound = state.results.filter(b => b.teamMembers && b.teamMembers.length > 0).length;
        const businessesWithWebsites = state.results.filter(b => b.website && b.website !== '#').length;
        updateStep('analyzing', 'in-progress', `Found contacts for ${contactsFound}/${businessesWithWebsites} businesses`);
      }
      
      return mergedContacts;
    } catch (error) {
      console.error('Error finding business contacts:', error);
      
      // Don't update state for cancelled requests
      if (axios.isCancel(error)) {
        console.log('Contact request cancelled:', error.message);
        return;
      }
      
      // Update the business to show the error
      setState(prevState => ({
        ...prevState,
        results: prevState.results.map((item, index) => 
          index === businessIndex 
            ? { 
                ...item, 
                isLoadingContacts: false,
                contactsError: error instanceof Error ? error.message : 'An error occurred'
              } 
            : item
        )
      }));
      
      // Update the workflow step if we're in the analyzing step
      if (state.currentStepId === 'analyzing') {
        const contactsFound = state.results.filter(b => b.teamMembers && b.teamMembers.length > 0).length;
        const businessesWithWebsites = state.results.filter(b => b.website && b.website !== '#').length;
        updateStep('analyzing', 'in-progress', `Found contacts for ${contactsFound}/${businessesWithWebsites} businesses`);
      }
      
      // Even if there's an error, we should return any existing contacts
      const existingTeamMembers = state.results[businessIndex]?.teamMembers || [];
      return { 
        index: businessIndex, 
        contacts: existingTeamMembers, 
        error: error instanceof Error ? error.message : 'An error occurred finding contacts' 
      };
    }
  }, [state.results, state.currentStepId, updateStep]);

  // Function to reload a specific business with all its information
  const reloadBusiness = useCallback(async (businessIndex: number) => {
    try {
      // Get the business from the state
      const business = state.results[businessIndex];
      
      if (!business) {
        console.error('Cannot reload business: Business not found');
        return;
      }
      
      // Update the business to show it's loading
      setState(prevState => ({
        ...prevState,
        results: prevState.results.map((item, index) => 
          index === businessIndex 
            ? { 
                ...item, 
                isLoadingBusiness: true, 
                businessError: undefined, 
                businessMessage: undefined,
                isLoadingContacts: true,
                contactsError: undefined,
                contactsMessage: undefined
              } 
            : item
        )
      }));
      
      // Reload the business data
      const result = await apiService.reloadBusiness(business, apiService.createCancelToken());
      
      // Extract the updated business and contacts
      const updatedBusiness = result.business;
      const newContacts = result.contacts;
      
      // Get existing team members
      const existingTeamMembers = business.teamMembers || [];
      
      // Merge existing and new contacts
      const mergedContacts = [...existingTeamMembers];
      
      // Process each new contact
      newContacts.forEach(newContact => {
        // Try to find an existing contact with the same name
        const existingContactIndex = mergedContacts.findIndex(
          contact => contact.name.toLowerCase() === newContact.name.toLowerCase()
        );
        
        if (existingContactIndex >= 0) {
          // Merge with existing contact - keep existing data but add any new data
          const existingContact = mergedContacts[existingContactIndex];
          
          // Create merged contact by combining both, with new data taking precedence only if existing data is empty
          mergedContacts[existingContactIndex] = {
            ...existingContact,
            role: existingContact.role || newContact.role,
            email: existingContact.email || newContact.email,
            phone: existingContact.phone || newContact.phone,
            bio: existingContact.bio || newContact.bio,
            // Merge social links without duplicates
            socialLinks: [
              ...(existingContact.socialLinks || []),
              ...(newContact.socialLinks || []).filter(
                link => !(existingContact.socialLinks || []).includes(link)
              )
            ]
          };
        } else {
          // Add new contact
          mergedContacts.push(newContact);
        }
      });
      
      // Update the business with the merged data
      setState(prevState => ({
        ...prevState,
        results: prevState.results.map((item, index) => 
          index === businessIndex 
            ? { 
                ...item,
                // Update business fields with new data, keeping existing data if new data is empty
                name: updatedBusiness.name || item.name,
                address: updatedBusiness.address || item.address,
                phone: updatedBusiness.phone || item.phone,
                website: updatedBusiness.website || item.website,
                // Keep the original description and store the new one in aboutUs
                aboutUs: updatedBusiness.description || item.aboutUs,
                socialLinks: updatedBusiness.socialLinks || item.socialLinks,
                // Update businessSocialLinks by merging existing and new links
                businessSocialLinks: [
                  ...(item.businessSocialLinks || []),
                  ...(updatedBusiness.socialLinks || []).filter(
                    link => !(item.businessSocialLinks || []).includes(link)
                  )
                ],
                // Update team members
                teamMembers: mergedContacts,
                // Reset loading states
                isLoadingBusiness: false,
                isLoadingContacts: false
              } 
            : item
        )
      }));
      
    } catch (error) {
      console.error('Error reloading business:', error);
      
      // Don't update state for cancelled requests
      if (axios.isCancel(error)) {
        console.log('Business reload request cancelled:', error.message);
        return;
      }
      
      // Update the business to show the error
      setState(prevState => ({
        ...prevState,
        results: prevState.results.map((item, index) => 
          index === businessIndex 
            ? { 
                ...item, 
                isLoadingBusiness: false,
                isLoadingContacts: false,
                businessError: error instanceof Error ? error.message : 'An error occurred reloading business',
                contactsError: error instanceof Error ? error.message : 'An error occurred finding contacts'
              } 
            : item
        )
      }));
    }
  }, [state.results]);

  // Function to cancel the current workflow
  const cancelWorkflow = useCallback(() => {
    cancelRef.current = true;
    
    // Cancel all active API requests
    apiService.cancelAllRequests();
    
    setState(prevState => ({
      ...prevState,
      currentStepId: null,
      error: 'Workflow cancelled by user',
      isComplete: true
    }));
    
    // Stop auto-loading
    setIsAutoLoadingMore(false);
  }, []);

  // Function to export results to CSV and zip
  const exportResultsToCSV = (results: LeadData[]): void => {
    try {
      // Create business CSV content
      let businessCSV = 'Business Name,Address,Email,Phone,Website,Description,Social Links\n';
      
      // Create contacts CSV content
      let contactsCSV = 'Business Name,Business Address,Business Website,Contact Name,Role,Email,Phone,Bio,Social Links\n';
      
      results.forEach(business => {
        // Add business to business CSV
        const businessSocialLinks = business.businessSocialLinks ? business.businessSocialLinks.join('; ') : '';
        businessCSV += `"${business.name}","${business.address}","${business.email || ''}","${business.phone || ''}","${business.website || ''}","${business.description?.replace(/"/g, '""') || ''}","${businessSocialLinks}"\n`;
        
        // Add contacts to contacts CSV
        if (business.teamMembers && business.teamMembers.length > 0) {
          business.teamMembers.forEach(member => {
            const memberSocialLinks = member.socialLinks ? member.socialLinks.join('; ') : '';
            contactsCSV += `"${business.name}","${business.address}","${business.website || ''}","${member.name}","${member.role || ''}","${member.email || ''}","${member.phone || ''}","${member.bio?.replace(/"/g, '""') || ''}","${memberSocialLinks}"\n`;
          });
        }
      });
      
      // Create Blob objects for the CSVs
      const businessBlob = new Blob([businessCSV], { type: 'text/csv;charset=utf-8;' });
      const contactsBlob = new Blob([contactsCSV], { type: 'text/csv;charset=utf-8;' });
      
      // Generate a filename based on search criteria
      const generateFilename = () => {
        if (!formData) return 'sinqleads_export';
        
        const { location, radius, instructions } = formData;
        const locationPart = location.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20);
        const instructionsPart = instructions 
          ? '_' + instructions.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20) 
          : '';
        
        return `sinqleads_${locationPart}_${radius}mi${instructionsPart}`.toLowerCase();
      };
      
      const filename = generateFilename();
      
      // Create a zip file using JSZip
      import('jszip').then(({ default: JSZip }) => {
        const zip = new JSZip();
        
        // Add files to the zip
        zip.file('businesses.csv', businessBlob);
        zip.file('contacts.csv', contactsBlob);
        
        // Generate the zip file
        zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
          // Create a download link and trigger the download
          const url = URL.createObjectURL(content);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${filename}.zip`;
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);
        });
      });
    } catch (error) {
      console.error('Error exporting results:', error);
      alert('An error occurred while exporting results. Please try again.');
    }
  };

  // Function to load more results using the conversation ID
  const loadMoreResults = useCallback(async (isAutoLoading = false) => {
    if (!state.formData || !state.id) {
      console.error('Cannot load more results: Missing form data or conversation ID');
      return;
    }

    try {
      // Set auto-loading flag if this is the first call in the auto-loading sequence
      if (!isAutoLoading && state.formData.maxResults && state.results.length < state.formData.maxResults) {
        setIsAutoLoadingMore(true);
      }

      // Update UI to show loading state
      setState(prevState => ({
        ...prevState,
        currentStepId: 'finding',
      }));
      
      updateStep('finding', 'in-progress', 'Loading more businesses...');
      
      // Call the API to get more results
      const moreBusinesses = await apiService.findBusinessesInArea(
        state.formData,
        state.id,
        apiService.createCancelToken()
      );
      
      // Update the conversation ID with the new one
      const id = moreBusinesses.id || state.id;
      
      // Check if the API indicates no more results
      if (moreBusinesses.noMoreResults) {
        console.log('API indicates no more results are available');
        setNoMoreResults(true);
        
        // Update the step message to indicate no more results
        const totalBusinesses = state.results.length;
        updateStep('finding', 'completed', `Found ${totalBusinesses} businesses total. No more businesses matching your criteria.`);
        
        // Update state to remove loading indicator
        setState(prevState => ({
          ...prevState,
          currentStepId: null
        }));
        
        setIsAutoLoadingMore(false);
        return [];
      }
      
      // Create new business results
      const newBusinessResults: LeadData[] = moreBusinesses.map(business => ({
        ...business,
        teamMembers: [],
        businessSocialLinks: business.socialLinks || [],
        isLoadingContacts: false
      }));
      
      // Store the original results length before adding new results
      const originalResultsLength = state.results.length;
      
      // Calculate total businesses found
      const totalBusinesses = originalResultsLength + newBusinessResults.length;
      
      // Update state with the new results
      setState(prevState => ({
        ...prevState,
        results: [...prevState.results, ...newBusinessResults],
        id,
        currentStepId: 'analyzing'
      }));
      
      updateStep('finding', 'completed', `Found ${totalBusinesses} businesses total`);
      
      // Calculate the updated total count using the current state and new results
      const updatedResultsCount = originalResultsLength + newBusinessResults.length;
      const targetResultsCount = state.formData?.maxResults || 10;
      
      console.log(`Auto-loading progress: ${updatedResultsCount}/${targetResultsCount} results`);
      
      // Check if we need to continue loading more results
      // If we do, start fetching the next batch in parallel with contact finding
      let nextBatchPromise: Promise<void> | null = null;
      
      if (updatedResultsCount < targetResultsCount && newBusinessResults.length > 0) {
        console.log(`Starting to fetch next batch in parallel: ${updatedResultsCount}/${targetResultsCount}`);
        
        // Start fetching the next batch in parallel IMMEDIATELY, before finding contacts
        nextBatchPromise = new Promise<void>((resolve) => {
          // Wait a moment before loading more to avoid overwhelming the API
          setTimeout(async () => {
            try {
              // Continue loading more results
              if (loadMoreResultsRef.current) {
                await loadMoreResultsRef.current(true);
              }
            } catch (error) {
              console.error('Error continuing auto-loading more results:', error);
            } finally {
              resolve();
            }
          }, 500);
        });
      } else if (newBusinessResults.length === 0) {
        // Stop auto-loading if we got no new results (we've reached the end)
        console.log('No more results available, stopping auto-loading');
        setIsAutoLoadingMore(false);
        setNoMoreResults(true);
        
        // Update the step message to indicate no more results
        updateStep('finding', 'completed', `Found ${totalBusinesses} businesses total. No more businesses matching your criteria.`);
      } else {
        // We've reached or exceeded the target count, stop auto-loading
        console.log(`Target reached: ${updatedResultsCount}/${targetResultsCount} results`);
        setIsAutoLoadingMore(false);
      }
      
      // Find contacts for the new businesses with websites
      updateStep('analyzing', 'in-progress', 'Finding contacts for new businesses...');
      
      const newBusinessesWithWebsites = newBusinessResults.filter(business => 
        business.website && business.website !== '#'
      );
      
      // Process contacts for the current batch
      if (newBusinessesWithWebsites.length > 0) {
        try {
          // Set isLoadingContacts to true for all new businesses with websites before starting the contact fetching process
          setState(prevState => ({
            ...prevState,
            results: prevState.results.map(item => {
              // Check if this item is one of the new businesses with websites
              const isNewBusinessWithWebsite = newBusinessesWithWebsites.some(
                b => b.name === item.name && b.address === item.address
              );
              
              return isNewBusinessWithWebsite 
                ? { ...item, isLoadingContacts: true } 
                : item;
            })
          }));
          
          let completedCount = 0;
          
          // Process businesses in parallel
          const contactPromises = newBusinessesWithWebsites.map(async (business) => {
            try {
              // Calculate the correct index in the combined results array
              // This is the original results length plus the index in the new results
              const businessIndex = originalResultsLength + newBusinessResults.indexOf(business);
              
              // Create instructions from form data
              const instructions = state.formData ? 
                `Find contacts for ${business.name} according to these criteria: ${state.formData.instructions || 'No specific criteria'}` : 
                undefined;
              
              const newContacts = await apiService.findContacts(
                business.website!, 
                apiService.createCancelToken(),
                instructions,
                business.socialLinks || []
              );
              
              // Increment completed count and update step message
              completedCount++;
              updateStep('analyzing', 'in-progress', `Found contacts for ${completedCount}/${newBusinessesWithWebsites.length} new businesses`);
              
              // Get existing team members (should be empty for new businesses, but just in case)
              const existingTeamMembers = state.results[businessIndex]?.teamMembers || [];
              
              // Merge existing and new contacts
              const mergedContacts = [...existingTeamMembers];
              
              // Process each new contact
              newContacts.forEach(newContact => {
                // Try to find an existing contact with the same name
                const existingContactIndex = mergedContacts.findIndex(
                  contact => contact.name.toLowerCase() === newContact.name.toLowerCase()
                );
                
                if (existingContactIndex >= 0) {
                  // Merge with existing contact - keep existing data but add any new data
                  const existingContact = mergedContacts[existingContactIndex];
                  
                  // Create merged contact by combining both, with new data taking precedence only if existing data is empty
                  mergedContacts[existingContactIndex] = {
                    ...existingContact,
                    role: existingContact.role || newContact.role,
                    email: existingContact.email || newContact.email,
                    phone: existingContact.phone || newContact.phone,
                    bio: existingContact.bio || newContact.bio,
                    // Merge social links without duplicates
                    socialLinks: [
                      ...(existingContact.socialLinks || []),
                      ...(newContact.socialLinks || []).filter(
                        link => !(existingContact.socialLinks || []).includes(link)
                      )
                    ]
                  };
                } else {
                  // Add new contact
                  mergedContacts.push(newContact);
                }
              });
              
              // Update this business's contacts immediately as they're found
              setState(prevState => ({
                ...prevState,
                results: prevState.results.map(item => {
                  // Find the correct business by matching name and address
                  const isMatchingBusiness = item.name === business.name && item.address === business.address;
                  return isMatchingBusiness
                    ? { 
                        ...item, 
                        teamMembers: mergedContacts,
                        isLoadingContacts: false,
                        contactsMessage: mergedContacts.length === 0 ? 'No contacts found' : undefined,
                        // Store the description from find-contacts-crawl in aboutUs instead of overwriting description
                        aboutUs: newContacts.description || item.aboutUs,
                        email: newContacts.email || (item as { email?: string }).email,
                        businessSocialLinks: [
                          ...(item.businessSocialLinks || []),
                          ...(newContacts.socialLinks || []).filter(
                            link => !(item.businessSocialLinks || []).includes(link)
                          )
                        ],
                        // Add relevancy score if available
                        ...(newContacts.relevancyScore !== undefined && { 
                          matchesInstructions: newContacts.relevancyScore > 50,
                          relevancyScore: newContacts.relevancyScore
                        })
                      } 
                    : item;
                })
              }));
              
              // Find the correct index in the results array for the return value
              const resultIndex = state.results.findIndex(item => 
                item.name === business.name && item.address === business.address
              );
              
              return { index: resultIndex !== -1 ? resultIndex : newBusinessesWithWebsites.indexOf(business), contacts: mergedContacts, error: null };
            } catch (error) {
              console.error(`Error finding contacts for ${business.name}:`, error);
              
              // Calculate the correct index in the combined results array
              const businessIndex = originalResultsLength + newBusinessResults.indexOf(business);
              
              // Still increment completed count even on error
              completedCount++;
              updateStep('analyzing', 'in-progress', `Found contacts for ${completedCount}/${newBusinessesWithWebsites.length} new businesses`);
              
              // Update this business with the error immediately
              setState(prevState => ({
                ...prevState,
                results: prevState.results.map(item => {
                  // Find the correct business by matching name and address
                  const isMatchingBusiness = item.name === business.name && item.address === business.address;
                  return isMatchingBusiness
                    ? { 
                        ...item, 
                        isLoadingContacts: false,
                        contactsError: error instanceof Error ? error.message : 'An error occurred finding contacts'
                      } 
                    : item;
                })
              }));
              
              // Even if there's an error, we should return any existing contacts
              const existingTeamMembers = state.results[businessIndex]?.teamMembers || [];
              return { 
                index: businessIndex, 
                contacts: existingTeamMembers, 
                error: error instanceof Error ? error.message : 'An error occurred finding contacts' 
              };
            }
          });
          
          // Wait for all contact finding operations to complete
          await Promise.all(contactPromises);
          
          // After all contacts have been processed, update the stats
          // Get the updated state after all the async operations
          const updatedState = (() => {
            // Count businesses with contacts in the entire result set
            const businessesWithContacts = state.results.filter(business => 
              business.teamMembers && business.teamMembers.length > 0
            ).length;
            
            // Count businesses with websites in the entire result set
            const businessesWithWebsites = state.results.filter(business => 
              business.website && business.website !== '#'
            ).length;
            
            return { businessesWithContacts, businessesWithWebsites };
          })();
          
          updateStep('analyzing', 'completed', `Found contacts for ${updatedState.businessesWithContacts}/${updatedState.businessesWithWebsites} businesses`);
        } catch (error) {
          console.error('Error finding contacts for new businesses:', error);
          updateStep('analyzing', 'error', 'Error finding contacts for new businesses');
        }
      } else {
        updateStep('analyzing', 'completed', 'No websites available to find contacts in new businesses');
      }
      
      // Set current step to null to indicate we're done with this batch
      setState(prevState => ({
        ...prevState,
        currentStepId: null
      }));
      
      // If we started a next batch fetch, we don't need to do anything else
      // The next batch is already being processed in parallel
      if (!nextBatchPromise) {
        // If we didn't start a next batch fetch, make sure auto-loading is stopped
        setIsAutoLoadingMore(false);
      }
      
      return newBusinessResults;
    } catch (error) {
      console.error('Error loading more results:', error);
      
      // Don't update state for cancelled requests
      if (axios.isCancel(error)) {
        console.log('Load more request cancelled:', error.message);
        setIsAutoLoadingMore(false);
        return;
      }
      
      updateStep('finding', 'error', 'Error loading more results');
      
      setState(prevState => ({
        ...prevState,
        currentStepId: null,
        error: error instanceof Error ? error.message : 'An error occurred loading more results'
      }));
      
      setIsAutoLoadingMore(false);
    }
  }, [state.formData, state.id, updateStep, state.results]);

  // Store the loadMoreResults function in the ref
  useEffect(() => {
    loadMoreResultsRef.current = loadMoreResults;
  }, [loadMoreResults]);

  return {
    state,
    formData,
    startWorkflow,
    resetWorkflow,
    findBusinessContacts,
    cancelWorkflow,
    exportResultsToCSV,
    loadMoreResults,
    reloadBusiness,
    isAutoLoadingMore,
    noMoreResults
  };
}; 